type ValueControl =
  | HTMLButtonElement
  | HTMLInputElement
  | HTMLSelectElement
  | HTMLTextAreaElement;

interface ControlState {
  control: ValueControl;
  onValueChange: () => void;
}

const CONTROL_SELECTOR = "button, input, select, textarea";
const IGNORE_SELECTOR = "[data-model-replace-ignore]";

function isValueControl(element: Element): element is ValueControl {
  return (
    element instanceof HTMLButtonElement ||
    element instanceof HTMLInputElement ||
    element instanceof HTMLSelectElement ||
    element instanceof HTMLTextAreaElement
  );
}

export class FormControlValueReplacer {
  private readonly states = new Map<ValueControl, ControlState>();
  private enabled = true;
  private replacementCount = 0;

  constructor(private readonly replaceText: (value: string) => string) {}

  configure(enabled: boolean): void {
    this.enabled = enabled;
  }

  start(): void {
    this.stop();
    this.replacementCount = 0;
  }

  scan(root: Node): number {
    if (!this.enabled) return 0;

    this.removeDisconnectedControls();
    let replacements = 0;
    if (root instanceof Element && isValueControl(root)) {
      replacements += this.register(root);
    }

    if (
      root instanceof Document ||
      root instanceof DocumentFragment ||
      root instanceof Element
    ) {
      for (const element of root.querySelectorAll(CONTROL_SELECTOR)) {
        if (isValueControl(element)) replacements += this.register(element);
      }
    }

    return replacements;
  }

  handleMutation(mutation: MutationRecord): void {
    if (!this.enabled) return;

    const target = mutation.target;
    if (target instanceof Element && isValueControl(target)) {
      this.register(target);
      return;
    }

    const option = target instanceof HTMLOptionElement
      ? target
      : target.parentElement?.closest("option");
    const select = option?.closest("select");
    if (select instanceof HTMLSelectElement) {
      this.syncControl(select);
    }

    this.removeDisconnectedControls();
  }

  get replacedCount(): number {
    return this.replacementCount;
  }

  stop(): void {
    for (const state of this.states.values()) this.removeState(state);
    this.states.clear();
  }

  private register(control: ValueControl): number {
    if (control.closest(IGNORE_SELECTOR)) return 0;

    if (!this.states.has(control)) {
      const state: ControlState = {
        control,
        onValueChange: () => this.syncControl(control),
      };
      control.addEventListener("input", state.onValueChange);
      control.addEventListener("change", state.onValueChange);
      this.states.set(control, state);
    }

    return this.syncControl(control);
  }

  private syncControl(control: ValueControl): number {
    if (!control.isConnected || control.closest(IGNORE_SELECTOR)) return 0;

    let changed = false;
    if (control instanceof HTMLSelectElement) {
      for (const option of control.options) {
        changed = this.replaceOption(option) || changed;
      }
    } else if (!(control instanceof HTMLInputElement && control.type === "file")) {
      changed = this.replaceValue(control) || changed;
      if (
        control instanceof HTMLInputElement ||
        control instanceof HTMLTextAreaElement
      ) {
        changed = this.replaceDefaultValue(control) || changed;
      }
    }

    if (
      (control instanceof HTMLInputElement ||
        control instanceof HTMLTextAreaElement) &&
      control.placeholder
    ) {
      const placeholder = this.replaceText(control.placeholder);
      if (placeholder !== control.placeholder) {
        control.placeholder = placeholder;
        changed = true;
      }
    }

    if (changed) this.replacementCount += 1;
    return changed ? 1 : 0;
  }

  private replaceValue(control: Exclude<ValueControl, HTMLSelectElement>): boolean {
    const value = this.replaceText(control.value);
    if (value === control.value) return false;
    control.value = value;
    return true;
  }

  private replaceDefaultValue(
    control: HTMLInputElement | HTMLTextAreaElement,
  ): boolean {
    const value = this.replaceText(control.defaultValue);
    if (value === control.defaultValue) return false;
    control.defaultValue = value;
    return true;
  }

  private replaceOption(option: HTMLOptionElement): boolean {
    let changed = false;
    const value = this.replaceText(option.value);
    if (value !== option.value) {
      option.value = value;
      changed = true;
    }

    if (option.hasAttribute("label")) {
      const label = this.replaceText(option.label);
      if (label !== option.label) {
        option.label = label;
        changed = true;
      }
    }

    const text = option.textContent ?? "";
    const display = this.replaceText(text);
    if (display !== text) {
      option.textContent = display;
      changed = true;
    }
    return changed;
  }

  private removeDisconnectedControls(): void {
    for (const state of [...this.states.values()]) {
      if (!state.control.isConnected) this.removeState(state);
    }
  }

  private removeState(state: ControlState): void {
    state.control.removeEventListener("input", state.onValueChange);
    state.control.removeEventListener("change", state.onValueChange);
    this.states.delete(state.control);
  }
}
